
import { Component, Vue } from 'vue-property-decorator';

import axios from 'axios';
import xor from 'lodash/xor';

import { Auth0User, UserGroup } from '@/lib/models';

@Component
export default class UserGroupView extends Vue {
  private userGroup: UserGroup | null = null;
  private auth0Users: Auth0User[] = [];

  // IDs of all users who should be in the group when changes are saved,
  // including users who are already in the group
  private selectedUserIds: string[] = [];

  private isSavingChanges: boolean = false;

  private isLoading: boolean = false;
  private currentPage: number = 1;
  private totalRows: number = 0;
  private pageOptions: number[] = [5, 10, 15, 20];
  private perPage: number = 10;

  private userFields: any[] = [
    {
      key: 'selected',
      sortable: false,
    },
    {
      key: 'email',
      sortable: true,
    },
    {
      key: 'given_name',
      sortable: true,
      label: 'First Name',
    },
    {
      key: 'family_name',
      sortable: true,
      label: 'Last Name',
    },
    {
      key: 'app_metadata.title',
      sortable: true,
      label: 'Title',
    },
  ];

  private userFilter: any = {
    email: '',
    firstName: '',
    lastName: '',
  };

  private created(): void {
    this.getRouteData();
  }

  private getRouteData(): void {
    this.isLoading = true;
    axios.all([this.getUserGroup(), this.getExportedUsers()])
      .then(axios.spread((userGroupResponse, exportedUsersResponse) => {
        this.userGroup = userGroupResponse.data;
        this.auth0Users = exportedUsersResponse.data;
        this.totalRows = this.activeUsers.length;
        this.selectedUserIds = this.initialUserIds;
      })).finally(() => {
        this.isLoading = false;
      });
  }

  private async getUserGroup(): Promise<any> {
    const url: string = `${process.env.VUE_APP_BACKEND_API_HOST}/usergroups/${this.$route.params.userGroupId}`;
    const accessToken: string = await (this as any).$auth0.getAccessTokenSilently();
    return axios.get(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  }

  private async getExportedUsers(): Promise<any> {
    const url: string = `${process.env.VUE_APP_BACKEND_API_HOST}/new_users/export`;
    const accessToken: string = await (this as any).$auth0.getAccessTokenSilently();
    return axios.get(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  }

  private filterUsers(row: Auth0User, filter: any): boolean {
    if (filter.email !== '') {
      if (!row.email.toLowerCase().includes(filter.email.toLowerCase())) {
        return false;
      }
    }
    if (filter.firstName !== '') {
      if (!row.given_name.toLowerCase().includes(filter.firstName.toLowerCase())) {
        return false;
      }
    }
    if (filter.lastName !== '') {
      if (!row.family_name.toLowerCase().includes(filter.lastName.toLowerCase())) {
        return false;
      }
    }
    return true;
  }

  private get activeUsers(): Auth0User[] {
    return this.auth0Users.filter((user) => user.app_metadata.is_active && !user.app_metadata.is_archived);
  }

  private get initialUserIds(): string[] {
    if (this.userGroup === null || this.activeUsers.length === 0) {
      return [];
    }
    return this.activeUsers
      .filter((user) => user.app_metadata.user_group_ids.includes(this.userGroup!.id!))
      .map((user) => user.app_metadata.legacy_user_id);
  }

  private get initialUsers(): Auth0User[] {
    if (this.userGroup === null || this.activeUsers === null) {
      return [];
    }
    return this.activeUsers.filter((user) => this.initialUserIds.includes(user.app_metadata.legacy_user_id));
  }

  private get selectedUsers(): Auth0User[] {
    if (this.userGroup === null || this.activeUsers === null) {
      return [];
    }
    return this.activeUsers.filter((user) => this.selectedUserIds.includes(user.app_metadata.legacy_user_id));
  }

  private selectAllUsers(): void {
    this.selectedUserIds = this.activeUsers.map((user) => user.app_metadata.legacy_user_id);
    (this.$refs.userTable as any).selectAllRows();
  }

  private unselectAllUsers(): void {
    this.selectedUserIds = [];
    (this.$refs.userTable as any).clearSelected();
  }

  private onFiltered(filteredItems: any[]): void {
    this.totalRows = filteredItems.length;
    this.preselectRows();
  }

  // Gets called when rows are selected and unselected
  private updateSelectedRows(selectedVisibleRows: Auth0User[]): void {
    // Remove IDs from selectedUserIds
    const userIdsToRemove: string[] = [];
    const allVisibleRows: Auth0User[] = (this.$refs.userTable as any).computedItems;
    const allVisibleUserIds: string[] = allVisibleRows.map((row) => row.app_metadata.legacy_user_id);
    const selectedVisibleUserIds: string[] = selectedVisibleRows.map((row) => row.app_metadata.legacy_user_id);
    for (const userId of allVisibleUserIds) {
      if (this.selectedUserIds.includes(userId) && !selectedVisibleUserIds.includes(userId)) {
        userIdsToRemove.push(userId);
      }
    }
    this.selectedUserIds = this.selectedUserIds.filter((userId) => !userIdsToRemove.includes(userId));

    // Add IDs to selectedUserIds
    for (const row of selectedVisibleRows) {
      if (!this.selectedUserIds.includes(row.app_metadata.legacy_user_id)) {
        this.selectedUserIds.push(row.app_metadata.legacy_user_id);
      }
    }
  }

  // Called when the table is sorted, filtered, or paginated
  private preselectRows(): void {
    const visibleRows: Auth0User[] = (this.$refs.userTable as any).computedItems;
    const rowIndexesToSelect: number[] = [];
    for (let i = 0; i < visibleRows.length; i++) {
      if (this.selectedUserIds.includes(visibleRows[i].app_metadata.legacy_user_id)) {
        rowIndexesToSelect.push(i);
      }
    }
    this.$nextTick(() => {
      for (const index of rowIndexesToSelect) {
        (this.$refs.userTable as any).selectRow(index);
      }
    });
  }

  private get userIdsToBeAdded(): string[] {
    return this.selectedUserIds.filter((userId) => !this.initialUserIds.includes(userId));
  }

  private get userIdsToBeRemoved(): string[] {
    return this.initialUserIds.filter((userId) => !this.selectedUserIds.includes(userId));
  }

  private get usersToBeAdded(): Auth0User[] {
    return this.activeUsers.filter((user) => this.userIdsToBeAdded.includes(user.app_metadata.legacy_user_id));
  }

  private get usersToBeRemoved(): Auth0User[] {
    return this.activeUsers.filter((user) => this.userIdsToBeRemoved.includes(user.app_metadata.legacy_user_id));
  }

  private get haveChangesBeenMade(): boolean {
    return xor(this.initialUserIds, this.selectedUserIds).length !== 0;
  }

  private saveChanges(): void {
    this.isSavingChanges = true;
    this.selectGroupUsers()
      .then((response) => {
        const message: string = `Successfully saved changes to user group: ${this.userGroup!.name}`;
        this.$bvModal.msgBoxOk(message, {
          title: 'User Group Changes Saved',
          okVariant: 'success',
          centered: true,
        });
      }).catch((error) => {
        const message: string = error.response?.data?.message ?? error.message;
        this.$bvModal.msgBoxOk(message, {
          title: 'Error Saving Changes',
          okVariant: 'danger',
          centered: true,
        });
      }).finally(() => {
        this.isSavingChanges = false;
        this.getRouteData();
      });
  }

  private async selectGroupUsers(): Promise<any> {
    const url: string = `${process.env.VUE_APP_BACKEND_API_HOST}/new_users/select_group_users/${this.userGroup!.id}`;
    const data = {
      userIdsToAdd: this.userIdsToBeAdded,
      userIdsToRemove: this.userIdsToBeRemoved,
    };
    const accessToken: string = await (this as any).$auth0.getAccessTokenSilently();
    return axios.put(url, data, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  }
}
